import {
	HeadersInit,
	RequestMethod,
	ApiClientError,
	IApiClientConfig,
	IRequestParam,
	IApiEventHandlers,
	IRequestOptions,
	DEFAULT_TIMEOUT,
	DEFAULT_RETRY_DELAY,
	DEFAULT_GET_RETRIES,
	DEFAULT_POST_RETRIES,
} from '@shared/constants';
/**
 * A generic API client for handling HTTP requests with features like base URL configuration,
 * default headers, automatic retries, request timeout, and optional event logging.
 */
export class ApiClient {
	private readonly baseURL: string;
	private readonly defaultHeaders: HeadersInit;
	private readonly allowCredentials: boolean;

	/**
	 * Constructs an instance of the ApiClient.
	 * @param {IApiClientConfig} config - The configuration for the API client.
	 */
	constructor({
		baseURL,
		defaultHeaders = {},
		allowCredentials = false,
	}: IApiClientConfig) {
		if (!baseURL) {
			throw new Error('ApiClient: baseURL is a required parameter.');
		}
		this.baseURL = baseURL;
		this.defaultHeaders = {
			'Content-Type': 'application/json',
			...defaultHeaders,
		};
		this.allowCredentials = allowCredentials;
	}

	/**
	 * The core fetch method with retry and timeout logic.
	 * @private
	 */
	private async doFetch<T>(
		url: string,
		options: IRequestOptions,
		retryCount = 0,
	): Promise<T> {
		const startTimestamp = Date.now();
		this.triggerInitEvent(url, options, startTimestamp);

		const controller = new AbortController();
		const timeout = setTimeout(() => controller.abort(), options.timeout);

		try {
			const response = await this.executeFetch(url, options, controller);
			this.triggerResponseEvent(url, options, startTimestamp, response);
			return await this.handleResponse(url, options, response, retryCount);
		} catch (error) {
			return await this.handleError(url, options, error, retryCount);
		} finally {
			clearTimeout(timeout);
		}
	}

	/**
	 * Executes the fetch request with proper headers and configuration.
	 * @private
	 */
	private async executeFetch(
		url: string,
		options: IRequestOptions,
		controller: AbortController,
	): Promise<Response> {
		return await fetch(url, {
			method: options.method ?? RequestMethod.GET,
			headers: { ...this.defaultHeaders, ...options.headers },
			body: options.body ? JSON.stringify(options.body) : undefined,
			signal: controller.signal,
			...(this.allowCredentials && { credentials: 'include' }),
		});
	}

	/**
	 * Triggers the API call initialization event.
	 * @private
	 */
	private triggerInitEvent(
		url: string,
		options: IRequestOptions,
		startTimestamp: number,
	): void {
		options?.onApiCallInit?.({
			apiUrl: url,
			methodType: options.method,
			timeStamp: startTimestamp,
		});
	}

	/**
	 * Triggers the API call success or failure event.
	 * @private
	 */
	private triggerResponseEvent(
		url: string,
		options: IRequestOptions,
		startTimestamp: number,
		response: Response,
	): void {
		const eventsPayload = {
			apiUrl: url,
			methodType: options.method,
			timeStamp: startTimestamp,
			statusCode: response.status,
			responseTime: Date.now() - startTimestamp,
		};

		if (response.ok) {
			options?.onApiCallSuccess?.(eventsPayload);
		} else {
			options?.onApiCallFailed?.(eventsPayload);
		}
	}

	/**
	 * Handles successful and failed responses.
	 * @private
	 */
	private async handleResponse<T>(
		url: string,
		options: IRequestOptions,
		response: Response,
		retryCount: number,
	): Promise<T> {
		if (!response.ok) {
			return await this.handleFailedResponse(
				url,
				options,
				response,
				retryCount,
			);
		}

		if (response.status === 204) {
			return null as unknown as T;
		}

		return (await this.parseResponse(response)) as T;
	}

	/**
	 * Handles failed HTTP responses with retry logic.
	 * @private
	 */
	private async handleFailedResponse<T>(
		url: string,
		options: IRequestOptions,
		response: Response,
		retryCount: number,
	): Promise<T> {
		if (retryCount < (options.maxRetryLimit ?? 0) && response.status >= 500) {
			await new Promise((res) => setTimeout(res, options.retryDelay));
			return this.doFetch(url, options, retryCount + 1);
		}

		const errorResponse = await this.parseResponse(response);
		throw this.buildApiClientError(
			url,
			options.method,
			response.status,
			errorResponse,
		);
	}

	/**
	 * Handles errors during fetch execution.
	 * @private
	 */
	private async handleError<T>(
		url: string,
		options: IRequestOptions,
		error: unknown,
		retryCount: number,
	): Promise<T> {
		if (
			error instanceof Error &&
			error.name === 'AbortError' &&
			retryCount < (options.maxRetryLimit ?? 0)
		) {
			await new Promise((res) => setTimeout(res, options.retryDelay));
			return this.doFetch(url, options, retryCount + 1);
		}

		if (error instanceof ApiClientError) {
			throw error;
		}

		throw this.buildApiClientError(
			url,
			options.method,
			0,
			error instanceof Error
				? error.message
				: 'An unknown network error occurred.',
		);
	}

	/**
	 * Safely parses the response body as JSON or text.
	 * @private
	 */
	private async parseResponse(response: Response): Promise<unknown> {
		const contentType = response.headers.get('content-type') ?? '';
		if (contentType.includes('application/json')) {
			try {
				return await response.json();
			} catch {
				// Handle cases where content-type is json but body is empty or malformed
				return null;
			}
		}
		try {
			return await response.text();
		} catch {
			return null;
		}
	}

	/**
	 * Constructs a standardized ApiClientError object.
	 * @private
	 */
	private buildApiClientError<T>(
		url: string,
		method: RequestMethod,
		status: number,
		errorResponse: T | string | null,
	): ApiClientError {
		const message = `API request failed: ${method} ${url} with status ${status || 'N/A'}`;
		const error = new ApiClientError(
			message,
			status,
			url,
			method,
			errorResponse,
		);

		if (typeof errorResponse === 'object' && errorResponse !== null) {
			Object.assign(error, errorResponse);
		}

		return error;
	}

	/**
	 * Public method to make a generic request.
	 * @param {string} url - The URL path to append to the baseURL.
	 * @param {IRequestOptions} options - The request options.
	 * @returns {Promise<T>} A promise that resolves with the response data.
	 */
	public async request<T>(url: string, options: IRequestOptions): Promise<T> {
		const requestURL = `${this.baseURL}${url}`;
		return this.doFetch<T>(requestURL, options);
	}

	/**
	 * Performs a GET request.
	 * @param {string} url - The URL path.
	 * @param {IRequestParam} [data] - Optional request parameters like headers.
	 * @returns {Promise<T>}
	 */
	public async get<T>(
		url: string,
		data?: IRequestParam,
		eventHandlers?: IApiEventHandlers,
	): Promise<T> {
		const requestConfig: IRequestOptions = {
			method: RequestMethod.GET,
			headers: data?.headers,
			retryDelay: DEFAULT_RETRY_DELAY,
			maxRetryLimit: DEFAULT_GET_RETRIES,
			timeout: DEFAULT_TIMEOUT,
			...eventHandlers,
		};
		return this.request<T>(url, requestConfig);
	}

	/**
	 * Performs a POST request.
	 * @param {string} url - The URL path.
	 * @param {IRequestParam} data - Request parameters including body and optional headers.
	 * @returns {Promise<T>}
	 */
	public async post<T>(
		url: string,
		data: IRequestParam,
		eventHandlers?: IApiEventHandlers,
	): Promise<T> {
		const requestConfig: IRequestOptions = {
			method: RequestMethod.POST,
			headers: data.headers,
			body: data.body,
			retryDelay: DEFAULT_RETRY_DELAY,
			maxRetryLimit: DEFAULT_POST_RETRIES,
			timeout: DEFAULT_TIMEOUT,
			...eventHandlers,
		};
		return this.request<T>(url, requestConfig);
	}

	/**
	 * Performs a PUT request.
	 * @param {string} url - The URL path.
	 * @param {IRequestParam} data - Request parameters including body and optional headers.
	 * @returns {Promise<T>}
	 */
	public async put<T>(
		url: string,
		data: IRequestParam,
		eventHandlers?: IApiEventHandlers,
	): Promise<T> {
		const requestConfig: IRequestOptions = {
			method: RequestMethod.PUT,
			headers: data.headers,
			body: data.body,
			retryDelay: DEFAULT_RETRY_DELAY,
			maxRetryLimit: DEFAULT_POST_RETRIES,
			timeout: DEFAULT_TIMEOUT,
			...eventHandlers,
		};
		return this.request<T>(url, requestConfig);
	}

	/**
	 * Performs a DELETE request.
	 * @param {string} url - The URL path.
	 * @param {IRequestParam} [data] - Optional request parameters like headers or body.
	 * @returns {Promise<T>}
	 */
	public async delete<T>(
		url: string,
		data?: IRequestParam,
		eventHandlers?: IApiEventHandlers,
	): Promise<T> {
		const requestConfig: IRequestOptions = {
			method: RequestMethod.DELETE,
			headers: data?.headers,
			body: data?.body,
			retryDelay: DEFAULT_RETRY_DELAY,
			maxRetryLimit: DEFAULT_GET_RETRIES, // Typically idempotent like GET
			timeout: DEFAULT_TIMEOUT,
			...eventHandlers,
		};
		return this.request<T>(url, requestConfig);
	}
}
